import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash } from "@phosphor-icons/react";
import { MAX_CHILDREN, EMPTY_CHILD } from "@/lib/profileForm";

function ChildRow({ child, index, editable, onChange, onRemove }) {
  const set = (key, value) => onChange({ ...child, [key]: value });
  return (
    <div className="rounded-md border border-border bg-muted/30 p-4" data-testid={`child-entry-${index}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">Child {index + 1}</p>
        {editable && (
          <Button type="button" variant="ghost" size="sm" className="gap-1.5 text-destructive" onClick={onRemove} data-testid={`child-remove-${index}`}>
            <Trash size={14} weight="bold" /> Remove
          </Button>
        )}
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-[2fr_1fr_1fr]">
        <div className="space-y-1.5">
          <Label htmlFor={`child-name-${index}`}>Full name <span className="text-destructive">*</span></Label>
          <Input id={`child-name-${index}`} data-testid={`child-name-${index}`} maxLength={150} value={child.full_name} disabled={!editable}
            onChange={(e) => set("full_name", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Sex <span className="text-destructive">*</span></Label>
          <Select value={child.sex || ""} onValueChange={(v) => set("sex", v)} disabled={!editable}>
            <SelectTrigger data-testid={`child-sex-${index}`}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{["Male", "Female"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`child-dob-${index}`}>Date of birth <span className="text-destructive">*</span></Label>
          <Input id={`child-dob-${index}`} data-testid={`child-dob-${index}`} type="date" max={new Date().toISOString().slice(0, 10)}
            value={child.date_of_birth} disabled={!editable} onChange={(e) => set("date_of_birth", e.target.value)} />
        </div>
      </div>
    </div>
  );
}

export function ChildrenEditor({ childrenList, noChildren, editable, onChange }) {
  const kids = childrenList || [];
  const stash = useRef([]);
  const update = (next, none = "") => onChange({ children: next, no_children: none });
  const toggleNone = (on) => {
    if (on) { stash.current = kids; update([], "yes"); }
    else update(stash.current.length ? stash.current : [{ ...EMPTY_CHILD }]);
  };
  return (
    <div className="space-y-3 sm:col-span-2" data-testid="children-editor">
      <Label>Biological Children <span className="text-destructive">*</span></Label>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={noChildren === "yes"} disabled={!editable} data-testid="no-children-checkbox"
          onCheckedChange={(v) => toggleNone(!!v)} />
        I have no biological children
      </label>
      {noChildren !== "yes" && (
        <>
          {kids.map((c, i) => (
            <ChildRow key={i} child={c} index={i} editable={editable}
              onChange={(child) => update(kids.map((k, j) => (j === i ? child : k)))}
              onRemove={() => update(kids.filter((_, j) => j !== i))} />
          ))}
          {editable && kids.length < MAX_CHILDREN && (
            <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => update([...kids, { ...EMPTY_CHILD }])} data-testid="child-add">
              <Plus size={14} weight="bold" /> {kids.length ? "Add another child" : "Add child"}
            </Button>
          )}
          {editable && <p className="text-xs text-muted-foreground" data-testid="children-count">
            {kids.length} of {MAX_CHILDREN} children added
          </p>}
          {!editable && !kids.length && <p className="text-sm text-muted-foreground" data-testid="children-none-listed">No children listed</p>}
        </>
      )}
    </div>
  );
}
